import { Timestamp } from 'firebase/firestore';

export interface EntityToSummarizeMapping {
    repNum: number;
    orgUserKey: string | null;
    targets: {
        dailyTarget: number | null;
        monthlyTarget: number | null;
        weeklyTarget: number | null;
    };
}

export interface IqResponseMonthSummaryData {
    code: IqDebtTranCode;
    date: string;
    gross_amount: number;
    tax: number;
    rep: number;
    user?: number;
}

export interface IqResponseMonthData extends IqResponseMonthSummaryData {
    created: string | Date;
}

export interface MonthSummaryDatum {
    date: Timestamp | string;
    bankCharges: number;
    bankChargesTax: number;
    creditNote: number;
    creditNoteTax: number;
    discount: number;
    discountTax: number;
    interestCharge: number;
    interestChargeTax: number;
    invoice: number;
    invoiceTax: number;
    journalCredit: number;
    journalCreditTax: number;
    journalDebit: number;
    journalDebitTax: number;
    payment: number;
    paymentTax: number;
    rebates: number;
    rebatesTax: number;
    refund: number;
    refundTax: number;
    repKey: null | string;
}

export interface FirebaseMonthSummary extends FirebaseMonthSummaryDataFields {
    lastUpdated: Date;
    key: string;
    summaryIsForKey: string;
    configKey: string;
    targets: TargetSnapshot[];
    dateFrom: string;
    dateTo: string;
}

export interface FirebaseMonthSummaryDataFields {
    fullSales: MonthSummaryDatum[];
    beforeHourSales: MonthSummaryDatum[];
    afterHourSales: MonthSummaryDatum[];
    workingHourSales: MonthSummaryDatum[];
}

export interface FirebaseDaySummary {
    key: string;
    summaryIsForKey: string;
    configKey: string;
    targets: TargetSnapshot[];
    dateFrom: Date;
    dateTo: Date;
    lastUpdated: Date;
    fullSalesToday: DaySummaryDatum[];
    fullSalesYesterday: DaySummaryDatum[];
}

export interface TargetSnapshot {
    date: Date;
    monthTarget: number;
    weekTarget: number;
    dayTarget: number;
    target?: number;
}

// export type IqDebtTranCode = "IC" | "PM" | "IN" | "CN" | "RE" | "DS" | "JC" | "JD" | "RF" | "BC";
export type IqDebtTranCode = 'IT' | 'PM' | 'IN' | 'CN' | 'RE' | 'DS' | 'JC' | 'JD' | 'RF' | 'BC';
export type IqDebtTranCodeWithSalesOrder =
    | 'IT'
    | 'PM'
    | 'IN'
    | 'CN'
    | 'RE'
    | 'DS'
    | 'JC'
    | 'JD'
    | 'RF'
    | 'BC'
    | 'SO'
    | '--'; // '--' is an unrecognized code.

export interface IqDaySummaryTransactionMap {
    code: IqDebtTranCode;
    reference: string;
}

export interface UnparsedDaySummaryDatum {
    credit_note_gross: number;
    credit_note_nett: number;
    credit_note_tax: number;
    date: string;
    document: string;
    invoice_type: string;
    invoiced_compounded: number;
    invoiced_gross: number;
    invoiced_nett: number;
    invoiced_tax: number;
    is_revised_sales_order: boolean;
    is_superseded: boolean;
    key: number;
    nett_outstanding: number;
    reference_key: string;
    sales_order_compounded: number;
    sales_order_gross: number;
    sales_order_nett: number;
    sales_order_tax: number;
    type: string;
    user_key: number;
}

export interface DaySummaryDatum {
    creditNoteGross: number;
    creditNoteNett: number;
    creditNoteTax: number;
    date: Date;
    document: string;
    invoicedCompounded: number; // Graph invoice line.
    invoicedGross: number;
    invoicedNett: number;
    invoicedTax: number;
    invoiceType: 'I' | 'C' | 'S'; // TODO type safety this...
    isRevisedSalesOrder: boolean;
    isSuperseded: boolean;
    key: number | string;
    nettOutstanding: number;
    referenceKey: string;
    salesOrderCompounded: number; // Graph sales order line.
    salesOrderGross: number;
    salesOrderNett: number;
    salesOrderTax: number;
    transactionType: IqDebtTranCodeWithSalesOrder;
    type: 'invoice' | 'sales_o' | 'open_so' | 'debtTran';
    userKey: number;

    value: number; // Graph total transaction value.
    valueCompounded: number; // Graph total line.
    repKey?: number | string;
    /** The IQ client account (the branch's linkaccount when set); names come from the stored debtor list. Absent on lines saved before OE-265. */
    clientAccount?: string;
    referenceKeys?: string[]; // Carried over entry only.
    repKeys?: (number | string)[]; // Carried over entry only.
}
